import { subscribeDesktopRealtime, subscribeDesktopWaveformUpdates } from "./desktop-event-gateway";
import type {
  ParsedDesktopEvent,
  ParsedTranscriptEvent,
  ParsedTranscriptWord,
} from "./desktop-event-parser";
import { decodeBase64Pcm16 } from "./desktop-audio-assembler";
import { TranscriptTurnGrouper } from "./transcript-turn-grouper";
import { SpeakerParticipantRoster } from "./speaker-participant-roster";

import { electronModule } from "@/modules/electron";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { Uuid } from "@/domains/global/identifiers";
import { TranscriptSpeakerIdentificationContext } from "@/modules/audio-recording/transcript-types";
import {
  resolveTranscriptChannelFromSpeakerVariant,
  resolveTranscriptSpeakerIdentification,
  type TranscriptSpeakerIdentification,
} from "@/modules/audio-recording/transcript-speaker-identity";
import { PreparedRecording } from "@/store/audio/recording/prepared-recording-controller";
import { RecordingWindow } from "@/store/audio/recording/recording-window";
import pTimeout from "p-timeout";
import type { DesktopWaveformUpdate } from "@/modules/audio-recording/desktop-waveform-update";
import { DESKTOP_PCM_SAMPLE_RATE } from "./desktop-pcm-persistence";
import type {
  DesktopRecorderTranscriptFinalTurn,
  DesktopRecorderTranscriptPartialTurn,
  DesktopRecorderTranscriptTurn,
} from "@/store/audio/recording/media-transcriber/transcriber.types";
import {
  DesktopAudioRecordingStrategy,
  DesktopRecordingStartAttemptTrace,
  DesktopRecordingStartFailurePhase,
  DesktopRecordingStartFailureSource,
  StartRecordingOutcome,
  StartRecordingOutcomeKind,
} from "@/modules/electron/types";
import {
  DesktopRecordingStartFatalError,
  DesktopRecordingRestartRequiredError,
  buildDesktopRecordingStartAttemptLogInfo,
} from "@/store/audio/recording/media-controller/desktop-controller-errors";

const STOP_TIMEOUT_MS = 10_000;
const STOP_TIMEOUT_SENTINEL = Symbol("desktop-capture-stop-timeout");

export type DesktopCaptureCallbacks = {
  onTranscriptPartial: (args: {
    channel: 0 | 1;
    partialTurn: DesktopRecorderTranscriptPartialTurn;
  }) => void;
  onTranscriptFinal: (args: {
    channel: 0 | 1;
    finalTurn: DesktopRecorderTranscriptFinalTurn;
  }) => void;
  onAudioSamples?: (args: { samples: Int16Array }) => void;
  onWaveformUpdate?: (args: { update: DesktopWaveformUpdate }) => void;
  onDebugEvent?: (args: { data: Record<string, unknown>; kind: string }) => void;
};

type SessionActionArgs = {
  windowId?: string | null;
  audioRecordingId?: Uuid | null;
};

export class DesktopCaptureService {
  private windowId: string | null = null;
  private meetingEventWindowId: string | null = null;
  private uploadToken: string | null = null;
  private unsubscribe: (() => void) | null = null;
  private waveformUnsubscribe: (() => void) | null = null;
  private readonly turnGrouper = new TranscriptTurnGrouper();
  private readonly participantRoster = new SpeakerParticipantRoster();
  private stoppingPromise: Promise<void> | null = null;
  /**
   * Limits renderer-consumed realtime events to a detected meeting window.
   *
   * Whole-desktop starts leave this unset because the prepared whole-desktop
   * window id is a start target, not a reliable discriminator for realtime
   * packet window ids.
   */
  private activeWindowId: string | null = null;
  private calendarEventId?: string;
  private preparedRecording: PreparedRecording | null;
  private audioRecordingId: Uuid | null = null;
  private recordingStrategy: DesktopAudioRecordingStrategy =
    DesktopAudioRecordingStrategy.WebArtifact;
  private speakerIdentificationContext: TranscriptSpeakerIdentificationContext =
    TranscriptSpeakerIdentificationContext.Unknown;
  private isAttachedToRecoveredSession = false;
  private startAttemptTrace: DesktopRecordingStartAttemptTrace | null = null;
  private replacePreviousAudioRecordingId: Uuid | null = null;
  private shouldFilterInactiveWindowEvents = false;
  private windowIdResolved = false;
  private uploadTokenResolved = false;

  private callbacks: DesktopCaptureCallbacks;

  constructor(preparedRecording: PreparedRecording | null, callbacks: DesktopCaptureCallbacks) {
    this.preparedRecording = preparedRecording;
    this.callbacks = callbacks;
  }

  isRecordingWindowId(windowId?: string): boolean {
    return this.meetingEventWindowId === windowId;
  }

  getPreparationProgress(): {
    windowIdResolved: boolean;
    uploadTokenResolved: boolean;
  } {
    return {
      windowIdResolved: this.windowIdResolved,
      uploadTokenResolved: this.uploadTokenResolved,
    };
  }

  async prepare({
    associatedNoteId,
    calendarEventId,
    associatedAudioRecordingId,
    meetingEventWindowId,
    recordingWindow,
    recordingStrategy,
    startAttemptTrace,
    replacePreviousAudioRecordingId,
    abortSignal,
  }: {
    associatedAudioRecordingId: Uuid;
    associatedNoteId: Uuid | null;
    /** Correlation id from the meeting notification; may be a synthetic
     *  calendar id and is never used as a Recall SDK window id. */
    meetingEventWindowId?: string;
    recordingWindow?: RecordingWindow;
    calendarEventId?: string;
    recordingStrategy: DesktopAudioRecordingStrategy;
    startAttemptTrace?: DesktopRecordingStartAttemptTrace;
    replacePreviousAudioRecordingId?: Uuid;
    customVocabulary?: string[];
    abortSignal?: AbortSignal;
  }): Promise<void> {
    this.resetTranscriptState();
    this.windowIdResolved = false;
    this.uploadTokenResolved = false;
    if (!this.preparedRecording) {
      throw new Error("Desktop capture was not prepared");
    }
    const prepareStartedAt = Date.now();
    let windowIdResolved = false;
    let uploadTokenResolved = false;

    try {
      // The only place a recording window becomes a Recall SDK window id:
      // detected meetings already carry their id from the meeting-detected
      // event; everything else records the prepared whole-desktop window.
      // https://docs.recall.ai/docs/desktop-sdk
      const windowId =
        recordingWindow?.kind === "detected-meeting"
          ? recordingWindow.recallWindowId
          : await this.preparedRecording.wholeDesktopWindowId;
      windowIdResolved = true;
      this.windowIdResolved = true;
      if (abortSignal?.aborted) return;

      this.windowId = windowId;
      this.meetingEventWindowId = meetingEventWindowId ?? null;
      this.activeWindowId =
        recordingWindow?.kind === "detected-meeting" ? recordingWindow.recallWindowId : null;
      this.calendarEventId = calendarEventId;
      this.audioRecordingId = associatedAudioRecordingId;
      this.recordingStrategy = recordingStrategy;
      this.replacePreviousAudioRecordingId = replacePreviousAudioRecordingId ?? null;
      this.shouldFilterInactiveWindowEvents = recordingWindow?.kind === "detected-meeting";
      this.speakerIdentificationContext =
        recordingWindow?.kind === "detected-meeting"
          ? TranscriptSpeakerIdentificationContext.MeetingWindow
          : TranscriptSpeakerIdentificationContext.WholeDesktop;
      this.isAttachedToRecoveredSession = false;
      this.startAttemptTrace = startAttemptTrace ?? null;

      const recallUploadCredentials = await this.preparedRecording.recallUploadCredentials;
      uploadTokenResolved = Boolean(recallUploadCredentials.uploadToken);
      this.uploadTokenResolved = uploadTokenResolved;
      this.uploadToken = recallUploadCredentials.uploadToken;
      if (abortSignal?.aborted) return;

      logger.info({
        message: "[DesktopCaptureService] prepared",
        info: {
          audioRecordingId: associatedAudioRecordingId,
          associatedNoteId,
          meetingEventWindowId: meetingEventWindowId ?? null,
          recordingWindowKind: recordingWindow?.kind ?? "whole-desktop",
          recordingStrategy,
          replacePreviousAudioRecordingId: replacePreviousAudioRecordingId ?? null,
          windowId,
          windowIdResolved,
          uploadTokenResolved,
          prepareTotalMs: Date.now() - prepareStartedAt,
          ...buildDesktopRecordingStartAttemptLogInfo({
            startAttemptTrace,
          }),
        },
      });

      this.subscribeToDesktopEvents({ associatedAudioRecordingId });
    } catch (error) {
      logger.warn({
        message: "[DesktopCaptureService] prepare failed",
        info: {
          audioRecordingId: associatedAudioRecordingId,
          associatedNoteId,
          meetingEventWindowId: meetingEventWindowId ?? null,
          recordingStrategy,
          replacePreviousAudioRecordingId: replacePreviousAudioRecordingId ?? null,
          failureSource: DesktopRecordingStartFailureSource.RendererPrepare,
          failurePhase: windowIdResolved
            ? DesktopRecordingStartFailurePhase.UploadCredentials
            : DesktopRecordingStartFailurePhase.PrepareWindow,
          windowIdResolved,
          uploadTokenResolved,
          prepareTotalMs: Date.now() - prepareStartedAt,
          error: error instanceof Error ? error.message : String(error),
          ...buildDesktopRecordingStartAttemptLogInfo({
            startAttemptTrace,
          }),
        },
      });
      throw error;
    }
  }

  async attach({
    associatedAudioRecordingId,
    recordingStrategy,
  }: {
    associatedAudioRecordingId: Uuid;
    recordingStrategy: DesktopAudioRecordingStrategy;
  }): Promise<void> {
    this.resetTranscriptState();
    this.windowId = null;
    this.meetingEventWindowId = null;
    this.activeWindowId = null;
    this.calendarEventId = undefined;
    this.uploadToken = null;
    this.audioRecordingId = associatedAudioRecordingId;
    this.recordingStrategy = recordingStrategy;
    this.speakerIdentificationContext = TranscriptSpeakerIdentificationContext.Unknown;
    this.isAttachedToRecoveredSession = true;
    this.startAttemptTrace = null;
    this.replacePreviousAudioRecordingId = null;
    this.shouldFilterInactiveWindowEvents = false;
    this.windowIdResolved = false;
    this.uploadTokenResolved = false;

    this.subscribeToDesktopEvents({ associatedAudioRecordingId });
  }

  async start(abortSignal?: AbortSignal): Promise<void> {
    if (abortSignal?.aborted) return;
    if (!this.preparedRecording) {
      throw new Error("Desktop capture was not prepared");
    }
    if (!this.windowId || !this.uploadToken || !this.audioRecordingId) {
      throw new Error("Desktop capture not prepared");
    }
    logger.info({
      message: "[DesktopCaptureService] start requested",
      info: {
        audioRecordingId: this.audioRecordingId,
        recordingStrategy: this.recordingStrategy,
        meetingEventWindowId: this.meetingEventWindowId,
        replacePreviousAudioRecordingId: this.replacePreviousAudioRecordingId,
        windowIdResolved: this.windowIdResolved,
        uploadTokenResolved: this.uploadTokenResolved,
        ...buildDesktopRecordingStartAttemptLogInfo({
          startAttemptTrace: this.startAttemptTrace,
        }),
      },
    });
    this.callbacks.onDebugEvent?.({
      data: { windowId: this.windowId, meetingEventWindowId: this.meetingEventWindowId },
      kind: "recording-starting",
    });
    const startArgs = {
      audioRecordingId: this.audioRecordingId,
      windowId: this.windowId,
      uploadToken: this.uploadToken,
      recordingStrategy: this.recordingStrategy,
      calendarEventId: this.calendarEventId,
      startAttemptTrace: this.startAttemptTrace ?? undefined,
      expectedWindowId: this.activeWindowId ?? undefined,
    };
    const result = this.replacePreviousAudioRecordingId
      ? await electronModule.recall.replaceRecording({
          ...startArgs,
          previousAudioRecordingId: this.replacePreviousAudioRecordingId,
        })
      : await electronModule.recall.startRecording(startArgs);
    const outcome: StartRecordingOutcome = result;
    if (outcome.outcome === StartRecordingOutcomeKind.RestartRequired) {
      logger.warn({
        message: "[DesktopCaptureService] start returned restart required",
        info: {
          audioRecordingId: this.audioRecordingId,
          recordingStrategy: this.recordingStrategy,
          failureSource: DesktopRecordingStartFailureSource.RecallCoordinatorStart,
          failurePhase: DesktopRecordingStartFailurePhase.RecallStart,
          reason: outcome.reason,
          windowIdResolved: this.windowIdResolved,
          uploadTokenResolved: this.uploadTokenResolved,
          ...(outcome.errorDiagnostics ?? {}),
          ...buildDesktopRecordingStartAttemptLogInfo({
            startAttemptTrace: this.startAttemptTrace,
          }),
        },
      });
      throw new DesktopRecordingRestartRequiredError(outcome.reason, {
        failureSource: DesktopRecordingStartFailureSource.RecallCoordinatorStart,
        failurePhase: DesktopRecordingStartFailurePhase.RecallStart,
        startAttemptTrace: this.startAttemptTrace,
        noteId: null,
        meetingEventWindowId: this.meetingEventWindowId,
        recordingStrategy: this.recordingStrategy,
        windowIdResolved: this.windowIdResolved,
        uploadTokenResolved: this.uploadTokenResolved,
        recallSdkErrorDiagnostics: outcome.errorDiagnostics ?? null,
        restartSucceeded: true,
      });
    }
    if (outcome.outcome === StartRecordingOutcomeKind.Fatal) {
      logger.warn({
        message: "[DesktopCaptureService] start returned fatal",
        info: {
          audioRecordingId: this.audioRecordingId,
          recordingStrategy: this.recordingStrategy,
          failureSource: DesktopRecordingStartFailureSource.RecallCoordinatorRestart,
          failurePhase: DesktopRecordingStartFailurePhase.RecallRestart,
          error: outcome.error,
          windowIdResolved: this.windowIdResolved,
          uploadTokenResolved: this.uploadTokenResolved,
          ...(outcome.errorDiagnostics ?? {}),
          ...buildDesktopRecordingStartAttemptLogInfo({
            startAttemptTrace: this.startAttemptTrace,
          }),
        },
      });
      throw new DesktopRecordingStartFatalError(outcome.error, {
        failureSource: DesktopRecordingStartFailureSource.RecallCoordinatorRestart,
        failurePhase: DesktopRecordingStartFailurePhase.RecallRestart,
        startAttemptTrace: this.startAttemptTrace,
        noteId: null,
        meetingEventWindowId: this.meetingEventWindowId,
        recordingStrategy: this.recordingStrategy,
        windowIdResolved: this.windowIdResolved,
        uploadTokenResolved: this.uploadTokenResolved,
        recallSdkErrorMessage: outcome.error,
        recallSdkErrorDiagnostics: outcome.errorDiagnostics ?? null,
        restartSucceeded: false,
        restartError: outcome.error,
      });
    }
    if (abortSignal?.aborted) return;
    this.callbacks.onDebugEvent?.({
      data: { windowId: this.windowId, meetingEventWindowId: this.meetingEventWindowId },
      kind: "recording-started",
    });
  }

  async pause(): Promise<void> {
    await this.runSessionAction({
      directAction: ({ windowId }) => electronModule.recall.pause({ windowId }),
      recoveredSessionAction: ({ audioRecordingId }) =>
        electronModule.recall.pauseRecordingSession({
          audioRecordingId,
        }),
    });

    this.callbacks.onDebugEvent?.({
      data: {
        windowId: this.windowId,
        meetingEventWindowId: this.meetingEventWindowId,
        audioRecordingId: this.audioRecordingId,
      },
      kind: "recording-paused",
    });
  }

  async resume(): Promise<void> {
    await this.runSessionAction({
      directAction: ({ windowId }) => electronModule.recall.resume({ windowId }),
      recoveredSessionAction: ({ audioRecordingId }) =>
        electronModule.recall.resumeRecordingSession({
          audioRecordingId,
        }),
    });

    this.callbacks.onDebugEvent?.({
      data: {
        windowId: this.windowId,
        meetingEventWindowId: this.meetingEventWindowId,
        audioRecordingId: this.audioRecordingId,
      },
      kind: "recording-resumed",
    });
  }

  async stop(): Promise<void> {
    await this.ensureSdkStopped();
    this.callbacks.onDebugEvent?.({
      data: { windowId: this.windowId, meetingEventWindowId: this.meetingEventWindowId },
      kind: "recording-stopped",
    });
    this.cleanupAfterSession();
  }

  async cancel(): Promise<void> {
    await this.ensureSdkStopped();
    this.callbacks.onDebugEvent?.({
      data: { windowId: this.windowId, meetingEventWindowId: this.meetingEventWindowId },
      kind: "recording-cancelled",
    });
    this.cleanupAfterSession();
  }

  detachAfterExternalStop(): void {
    this.callbacks.onDebugEvent?.({
      data: {
        windowId: this.windowId,
        meetingEventWindowId: this.meetingEventWindowId,
        audioRecordingId: this.audioRecordingId,
      },
      kind: "recording-externally-stopped",
    });
    this.cleanupAfterSession();
  }

  dispose() {
    if (this.isAttachedToRecoveredSession) {
      this.cleanupAfterSession();
      return;
    }

    void this.cancel();
  }

  private async ensureSdkStopped(): Promise<void> {
    if (!this.windowId && !this.audioRecordingId) {
      return;
    }
    if (this.stoppingPromise) {
      await this.stoppingPromise;
      return;
    }

    const windowId = this.windowId;
    const audioRecordingId = this.audioRecordingId;
    this.stoppingPromise = (async () => {
      try {
        logger.info({
          message: "[DesktopCaptureService] stopping",
          info: { windowId, audioRecordingId },
        });

        const stopOperation = this.runSessionAction({
          windowId,
          audioRecordingId,
          directAction: ({ windowId: activeWindowId }) =>
            electronModule.recall.stop({ windowId: activeWindowId }),
          recoveredSessionAction: ({ audioRecordingId: activeAudioRecordingId }) =>
            electronModule.recall.stopRecordingSession({
              audioRecordingId: activeAudioRecordingId,
            }),
        });
        const stopResult = await pTimeout<symbol | void>(stopOperation, {
          milliseconds: STOP_TIMEOUT_MS,
          fallback: () => STOP_TIMEOUT_SENTINEL,
        });
        if (stopResult === STOP_TIMEOUT_SENTINEL) {
          logger.warn({
            message: "[DesktopCaptureService] stop timed out",
            info: { windowId, audioRecordingId, timeoutMs: STOP_TIMEOUT_MS },
          });
        }
      } catch (err) {
        logger.warn({
          message: "[DesktopCaptureService] stop failed",
          info: { error: objectModule.safeErrorAsJson(err as Error) },
        });
      } finally {
        if (this.windowId === windowId) {
          this.windowId = null;
          this.activeWindowId = null;
        }
        this.uploadToken = null;
        this.audioRecordingId = null;
        this.isAttachedToRecoveredSession = false;
        this.stoppingPromise = null;
      }
    })();
    await this.stoppingPromise;
  }

  private async runSessionAction({
    windowId = this.windowId,
    audioRecordingId = this.audioRecordingId,
    directAction,
    recoveredSessionAction,
  }: SessionActionArgs & {
    directAction: (args: { windowId: string }) => Promise<void>;
    recoveredSessionAction: (args: { audioRecordingId: Uuid }) => Promise<void>;
  }): Promise<void> {
    if (windowId) {
      await directAction({ windowId });
      return;
    }

    if (!audioRecordingId) {
      return;
    }

    await recoveredSessionAction({ audioRecordingId });
  }

  private cleanupAfterSession() {
    try {
      this.unsubscribe?.();
    } catch (_) {
      /* empty */
    }
    try {
      this.waveformUnsubscribe?.();
    } catch (_) {
      /* empty */
    }
    this.unsubscribe = null;
    this.waveformUnsubscribe = null;
    this.resetTranscriptState();
    this.windowId = null;
    this.meetingEventWindowId = null;
    this.calendarEventId = undefined;
    this.uploadToken = null;
    this.activeWindowId = null;
    this.audioRecordingId = null;
    this.isAttachedToRecoveredSession = false;
    this.startAttemptTrace = null;
    this.replacePreviousAudioRecordingId = null;
    this.shouldFilterInactiveWindowEvents = false;
    this.windowIdResolved = false;
    this.uploadTokenResolved = false;
    this.speakerIdentificationContext = TranscriptSpeakerIdentificationContext.Unknown;
  }

  private subscribeToDesktopEvents({
    associatedAudioRecordingId,
  }: {
    associatedAudioRecordingId: Uuid;
  }): void {
    this.unsubscribe?.();
    this.waveformUnsubscribe?.();

    this.unsubscribe = subscribeDesktopRealtime(event => this.onEvent(event));
    this.waveformUnsubscribe = subscribeDesktopWaveformUpdates(({ audioRecordingId, update }) => {
      if (audioRecordingId !== associatedAudioRecordingId) {
        return;
      }

      this.callbacks.onWaveformUpdate?.({ update });
    });
  }

  private onEvent(parsedEvent: ParsedDesktopEvent) {
    if (!parsedEvent) return;

    if (this.shouldIgnoreInactiveWindowEvent(parsedEvent)) return;

    if (parsedEvent.type === "audio_mixed_raw.data") {
      const samples = decodeBase64Pcm16({
        bufferBase64: parsedEvent.bufferBase64,
      });
      this.callbacks.onAudioSamples?.({ samples });
      this.callbacks.onWaveformUpdate?.({
        update: {
          kind: "pcm16",
          sampleRate: DESKTOP_PCM_SAMPLE_RATE,
          samples,
          durationMs: undefined,
        },
      });
      return;
    }

    if (
      parsedEvent.type === "participant_events.join" ||
      parsedEvent.type === "participant_events.update" ||
      parsedEvent.type === "participant_events.leave"
    ) {
      this.participantRoster.remember({
        participantId: parsedEvent.participantId ?? null,
        speakerParticipant: parsedEvent.speakerParticipant,
      });
      return;
    }

    if (parsedEvent.type !== "transcript.data" && parsedEvent.type !== "transcript.partial_data") {
      return;
    }

    const event = parsedEvent;
    const provisionalSpeakerIdentity = resolveTranscriptSpeakerIdentification({
      context: this.speakerIdentificationContext,
      recallSpeakerLabel: event.recallSpeakerLabel ?? null,
      participantId: event.participantId ?? null,
      speakerParticipant: event.speakerParticipant ?? null,
    });
    const channel = resolveTranscriptChannelFromSpeakerVariant({
      speakerVariant: provisionalSpeakerIdentity.speakerVariant,
    });

    const assignment = this.turnGrouper.assign({
      channel,
      speakerGroupingKey:
        event.speakerGroupingKey ?? buildParticipantSpeakerGroupingKey(event.participantId),
      utteranceKey: event.transcriptId ?? null,
      firstWordStartMs: firstFiniteWordStartMs(event.words),
      timestampMs: event.timestamp,
      isFinal: event.endOfTurn,
    });

    if (assignment.kind === "dropped") {
      return;
    }

    if (event.endOfTurn) {
      const finalTurn = this.buildTranscriptTurn({
        event,
        turnOrder: assignment.turnOrder,
      }) as DesktopRecorderTranscriptFinalTurn;

      this.callbacks.onTranscriptFinal({
        channel,
        finalTurn,
      });
      return;
    }

    const partialTurn = this.buildTranscriptTurn({
      event,
      turnOrder: assignment.turnOrder,
    }) as DesktopRecorderTranscriptPartialTurn;

    this.callbacks.onTranscriptPartial({
      channel,
      partialTurn,
    });
  }

  private shouldIgnoreInactiveWindowEvent(parsedEvent: NonNullable<ParsedDesktopEvent>): boolean {
    if (!this.shouldFilterInactiveWindowEvents) {
      return false;
    }

    if (!this.activeWindowId || !parsedEvent.windowId) {
      return false;
    }

    if (parsedEvent.windowId === this.activeWindowId) {
      return false;
    }

    logger.warn({
      message: "[DesktopCaptureService] dropped event from inactive Recall window",
      info: {
        audioRecordingId: this.audioRecordingId,
        activeWindowId: this.activeWindowId,
        receivedWindowId: parsedEvent.windowId,
        eventType: parsedEvent.type,
      },
    });

    return true;
  }

  private resetTranscriptState(): void {
    this.turnGrouper.reset();
    this.participantRoster.reset();
  }

  private buildTranscriptTurn({
    event,
    turnOrder,
  }: {
    event: ParsedTranscriptEvent;
    turnOrder: number;
  }): DesktopRecorderTranscriptTurn {
    const speakerParticipant = this.participantRoster.resolve({
      participantId: event.participantId ?? null,
      speakerParticipant: event.speakerParticipant,
    });
    const speakerIdentity = resolveTranscriptSpeakerIdentification({
      context: this.speakerIdentificationContext,
      recallSpeakerLabel: event.recallSpeakerLabel ?? null,
      participantId: event.participantId ?? null,
      speakerParticipant: speakerParticipant ?? null,
    });

    return {
      turnOrder,
      transcript: event.transcript,
      timestampMs: event.timestamp,
      isFormatted: false,
      isEndOfTurn: event.endOfTurn,
      endOfTurnConfidence: event.endOfTurn ? 1 : 0,
      participantId: event.participantId ?? null,
      ...(speakerParticipant ? { speakerParticipant } : {}),
      ...mapSpeakerIdentificationToTurnFields(speakerIdentity),
      words: event.words.map(word => ({
        text: word.text,
        startMs: word.start,
        endMs: word.end,
        confidence: word.confidence,
      })),
      raw: event,
    };
  }
}

function firstFiniteWordStartMs(words: readonly ParsedTranscriptWord[]): number | null {
  const firstWord = words[0];
  if (!firstWord || !Number.isFinite(firstWord.start)) {
    return null;
  }

  return firstWord.start;
}

function buildParticipantSpeakerGroupingKey(
  participantId: number | string | null | undefined
): string | null {
  if (participantId == null) return null;

  const id = String(participantId).trim();
  return id ? `participant:${id}` : null;
}

function mapSpeakerIdentificationToTurnFields(
  speakerIdentity: TranscriptSpeakerIdentification
): Pick<
  DesktopRecorderTranscriptTurn,
  | "speakerId"
  | "speakerVariant"
  | "speakerAudioSource"
  | "speakerIdentificationContext"
  | "speakerIdentificationReason"
> {
  return {
    speakerId: speakerIdentity.speakerId,
    speakerVariant: speakerIdentity.speakerVariant,
    speakerAudioSource: speakerIdentity.speakerAudioSource,
    speakerIdentificationContext: speakerIdentity.speakerIdentificationContext,
    speakerIdentificationReason: speakerIdentity.speakerIdentificationReason,
  };
}
